import { API_URL } from '../../config';
import PageHeader from '../../components/PageHeader';
// src/pages/CompanySettingsPage.tsx - FINAL MOBILE RESPONSIVE VERSION
import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Divider,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Stack,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Palette as PaletteIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  CheckCircle as CheckIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Refresh as ResetIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useThemeContext, themePresets, ThemeColors } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import LogoUploadSection from '../../components/LogoUploadSection';

import PortalLinkCard from '../../components/PortalLinkCard';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  isMobile?: boolean;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, isMobile = false, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: isMobile ? 2 : 3 }}>{children}</Box>}
    </div>
  );
}


// ─── Password Change Section ──────────────────────────────────────────────────

const PasswordChangeSection: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword,     setNewPassword]     = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [showCurrent,     setShowCurrent]     = React.useState(false);
  const [showNew,         setShowNew]         = React.useState(false);
  const [showConfirm,     setShowConfirm]     = React.useState(false);
  const [saving,          setSaving]          = React.useState(false);
  const [success,         setSuccess]         = React.useState('');
  const [error,           setError]           = React.useState('');

  const validate = () => {
    if (!currentPassword) return 'Current password is required';
    if (!newPassword)     return 'New password is required';
    if (newPassword.length < 8) return 'New password must be at least 8 characters';
    if (newPassword === currentPassword) return 'New password must be different from current';
    if (newPassword !== confirmPassword) return 'Passwords do not match';
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const apiUrl = API_URL;
      const res = await fetch(apiUrl + '/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to change password');
      setSuccess('Password changed successfully');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (e: any) {
      setError(e.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const strength = (() => {
    if (!newPassword) return null;
    if (newPassword.length < 8) return { label: 'Too short', color: 'error' as const, pct: '20%' };
    const s = [/[A-Z]/, /[a-z]/, /[0-9]/, /[^A-Za-z0-9]/].filter(r => r.test(newPassword)).length;
    if (s <= 2) return { label: 'Weak',   color: 'warning' as const, pct: '40%' };
    if (s === 3) return { label: 'Good',   color: 'info'    as const, pct: '70%' };
    return           { label: 'Strong', color: 'success' as const, pct: '100%' };
  })();

  return (
    <Paper sx={{ p: isMobile ? 1.5 : 3, width: '100%', boxSizing: 'border-box' }}>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <LockIcon color="primary" />
        <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="bold">Change Password</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Update your password regularly to keep your account secure.
      </Typography>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={2} sx={{ maxWidth: 480 }}>
        <Grid item xs={12}>
          <TextField fullWidth size={isMobile ? 'small' : 'medium'} label="Current Password"
            type={showCurrent ? 'text' : 'password'} value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            InputProps={{ endAdornment: (
              <IconButton size="small" onClick={() => setShowCurrent(s => !s)}>
                {showCurrent ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            )}} />
        </Grid>

        <Grid item xs={12}>
          <TextField fullWidth size={isMobile ? 'small' : 'medium'} label="New Password"
            type={showNew ? 'text' : 'password'} value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            InputProps={{ endAdornment: (
              <IconButton size="small" onClick={() => setShowNew(s => !s)}>
                {showNew ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            )}} />
          {strength && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ height: 4, bgcolor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: strength.pct, borderRadius: 2,
                  bgcolor: strength.color + '.main', transition: 'width 0.3s ease' }} />
              </Box>
              <Typography variant="caption" color={strength.color + '.main'} display="block" sx={{ mt: 0.5 }}>
                {strength.label}
              </Typography>
            </Box>
          )}
        </Grid>

        <Grid item xs={12}>
          <TextField fullWidth size={isMobile ? 'small' : 'medium'} label="Confirm New Password"
            type={showConfirm ? 'text' : 'password'} value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            error={!!confirmPassword && confirmPassword !== newPassword}
            helperText={confirmPassword && confirmPassword !== newPassword ? 'Passwords do not match' : ''}
            InputProps={{ endAdornment: (
              <IconButton size="small" onClick={() => setShowConfirm(s => !s)}>
                {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            )}} />
        </Grid>

        <Grid item xs={12}>
          <Button variant="contained" onClick={handleSubmit} startIcon={<LockIcon />}
            disabled={saving || !currentPassword || !newPassword || !confirmPassword}
            size={isMobile ? 'small' : 'medium'} fullWidth={isMobile}>
            {saving ? 'Changing Password…' : 'Change Password'}
          </Button>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />
      <Typography variant="body2" color="text.secondary" fontWeight={600} gutterBottom>
        Password requirements
      </Typography>
      {['At least 8 characters','Mix of uppercase and lowercase','At least one number','Special character recommended'].map(r => (
        <Typography key={r} variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>• {r}</Typography>
      ))}
    </Paper>
  );
};

const CompanySettingsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [currentTab, setCurrentTab] = useState(0);
  const { company, isRole } = useAuth();
  const {
    mode,
    currentPreset,
    customColors,
    toggleColorMode,
    setThemePreset,
    setCustomColors,
    resetToDefault,
    canEditTheme,
  } = useThemeContext();

  const [showCustom, setShowCustom] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [tempCustomColors, setTempCustomColors] = useState<ThemeColors>({
    primary: customColors?.primary || '#0D47A1',
    secondary: customColors?.secondary || '#FFC107',
    success: customColors?.success || '#4CAF50',
    warning: customColors?.warning || '#FF9800',
    error: customColors?.error || '#F44336',
    info: customColors?.info || '#2196F3',
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handlePresetSelect = async (presetId: string) => {
    if (!canEditTheme) return;
    setSaving(true);
    try {
      await setThemePreset(presetId);
      setShowCustom(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to set theme:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleApplyCustom = async () => {
    if (!canEditTheme) return;
    setSaving(true);
    try {
      await setCustomColors(tempCustomColors);
      setShowCustom(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to apply custom colors:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!canEditTheme) return;
    if (!window.confirm('Are you sure you want to reset to default theme?')) return;
    setSaving(true);
    try {
      await resetToDefault();
      setShowCustom(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to reset theme:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleDarkMode = async () => {
    if (!canEditTheme) return;
    setSaving(true);
    try {
      await toggleColorMode();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to toggle dark mode:', error);
    } finally {
      setSaving(false);
    }
  };

  const ColorPicker = ({ 
    label, 
    value, 
    onChange, 
    disabled 
  }: { 
    label: string; 
    value: string; 
    onChange: (color: string) => void;
    disabled?: boolean;
  }) => (
    <Box sx={{ mb: 2, width: '100%' }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%' }}>
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          style={{
            width: isMobile ? 50 : 60,
            height: isMobile ? 35 : 40,
            border: '2px solid #ddd',
            borderRadius: 8,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            flexShrink: 0,
          }}
        />
        <TextField
          value={value}
          onChange={(e) => onChange(e.target.value)}
          size="small"
          disabled={disabled}
          sx={{ flex: 1, minWidth: 0 }}
          placeholder="#000000"
        />
      </Box>
    </Box>
  );

  // Check if user has permission
  if (!canEditTheme) {
    return (
      <Container maxWidth="lg" sx={{ mt: isMobile ? 2 : 4, mb: isMobile ? 2 : 4, px: isMobile ? 1.5 : 3 }}>
        <Alert severity="warning" icon={<SecurityIcon />}>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom>
            Administrator Access Required
          </Typography>
          <Typography variant="body2">
            Only company administrators can access company settings.
            Contact your administrator if you need to modify company settings.
          </Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: '100vw',
      overflowX: 'hidden',
      boxSizing: 'border-box',
    }}>
      <Container 
        maxWidth="lg" 
        sx={{ 
          mt: isMobile ? 2 : 4, 
          mb: isMobile ? 2 : 4, 
          px: isMobile ? 1.5 : 3,
          width: '100%',
          maxWidth: isMobile ? '100%' : undefined,
          boxSizing: 'border-box',
        }}
      >
        {/* Header */}
        <PageHeader title="Company Settings" description="Manage company details, branding, and workspace preferences" icon="settings" />

        {/* Success Message */}
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2, width: '100%' }} onClose={() => setSaveSuccess(false)}>
            Settings saved successfully! Changes are now visible to all users.
          </Alert>
        )}

        {/* Tabs */}
        <Box sx={{ width: '100%', mb: isMobile ? 2 : 3 }}>
          <Paper sx={{ width: '100%', overflowX: 'hidden' }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              aria-label="company settings tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                width: '100%',
                '& .MuiTabs-flexContainer': {
                  gap: 0,
                },
                '& .MuiTab-root': {
                  minWidth: isMobile ? 100 : 120,
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                  px: isMobile ? 1 : 2,
                  py: isMobile ? 1 : 1.5,
                },
              }}
            >
              <Tab icon={<BusinessIcon />} label="General" />
              <Tab icon={<PaletteIcon />} label="Branding & Theme" />
              <Tab icon={<NotificationsIcon />} label="Notifications" />
              <Tab icon={<SecurityIcon />} label="Security" />
            </Tabs>
          </Paper>
        </Box>

        {/* Tab Panels */}
        
        {/* General Settings Tab */}
        <TabPanel value={currentTab} index={0} isMobile={isMobile}>
          <Paper sx={{ p: isMobile ? 1.5 : 3, width: '100%', boxSizing: 'border-box' }}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="bold" gutterBottom>
              Company Information
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Basic company information and settings.
            </Typography>
            
            <Box sx={{ width: '100%' }}>
              
              <Grid item xs={12} md={6}>
                <PortalLinkCard />
                </Grid>

              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Company Name"
                    value={company?.name || ''}
                    fullWidth
                    disabled
                    size={isMobile ? 'small' : 'medium'}
                    helperText={!isMobile && "Contact support to change company name"}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Company Email"
                    value={company?.email || ''}
                    fullWidth
                    disabled
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Domain"
                    value={company?.domain || ''}
                    fullWidth
                    disabled
                    size={isMobile ? 'small' : 'medium'}
                    helperText={!isMobile && `Access at: ${company?.domain}.scetru.com`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Timezone"
                    value={company?.settings?.timezone || 'UTC'}
                    fullWidth
                    disabled
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Grid>
              </Grid>
            </Box>
          </Paper>

          {/* Logo Upload Section */}
          <LogoUploadSection canEdit={canEditTheme} />
        </TabPanel>

        {/* Branding & Theme Tab */}
        <TabPanel value={currentTab} index={1} isMobile={isMobile}>
          {/* Theme Mode Section */}
          <Paper sx={{ p: isMobile ? 1.5 : 3, mb: isMobile ? 2 : 3, width: '100%', boxSizing: 'border-box' }}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="bold" gutterBottom>
              Appearance Mode
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {isMobile ? 'Choose between light and dark themes.' : 'Choose between light and dark themes. This affects all users in your organization.'}
            </Typography>

            <Card variant="outlined" sx={{ mt: 2, width: '100%' }}>
              <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={mode === 'dark'}
                      onChange={handleToggleDarkMode}
                      disabled={saving}
                      size={isMobile ? 'small' : 'medium'}
                      icon={<LightModeIcon />}
                      checkedIcon={<DarkModeIcon />}
                    />
                  }
                  label={
                    <Box sx={{ ml: 1 }}>
                      <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight={500}>
                        {mode === 'dark' ? 'Dark Mode' : 'Light Mode'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {mode === 'dark' 
                          ? 'Easier on the eyes in low-light'
                          : 'Better visibility in bright areas'}
                      </Typography>
                    </Box>
                  }
                />
              </CardContent>
            </Card>
          </Paper>

          {/* Color Theme Section */}
          <Paper sx={{ p: isMobile ? 1.5 : 3, width: '100%', boxSizing: 'border-box' }}>
            <Stack spacing={2} sx={{ width: '100%' }}>
              <Box>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="bold" gutterBottom>
                  Color Theme
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isMobile ? 'Select a theme or create custom colors.' : 'Select a preset theme or create your own custom colors for company branding.'}
                </Typography>
              </Box>
              
              {!showCustom && (
                <Button
                  variant="outlined"
                  startIcon={!isMobile && <PaletteIcon />}
                  onClick={() => setShowCustom(true)}
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth={isMobile}
                >
                  Custom Colors
                </Button>
              )}
            </Stack>

            {!showCustom ? (
              <>
                {/* Preset Themes Grid */}
                <Box sx={{ mt: 2, width: '100%' }}>
                  <Grid container spacing={isMobile ? 1.5 : 2}>
                    {themePresets.map((preset) => {
                      const isActive = !customColors && currentPreset.id === preset.id;
                      return (
                        <Grid item xs={6} sm={6} md={3} key={preset.id}>
                          <Card
                            variant="outlined"
                            sx={{
                              border: isActive ? 2 : 1,
                              borderColor: isActive ? 'primary.main' : 'divider',
                              position: 'relative',
                              transition: 'all 0.2s',
                              '&:hover': {
                                borderColor: 'primary.main',
                                boxShadow: 2,
                              },
                            }}
                          >
                            <CardActionArea
                              onClick={() => handlePresetSelect(preset.id)}
                              disabled={saving}
                            >
                              <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                                <Box sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'flex-start', 
                                  mb: 1.5
                                }}>
                                  <Typography variant={isMobile ? 'caption' : 'body1'} fontWeight={600} noWrap>
                                    {preset.name}
                                  </Typography>
                                  {isActive && (
                                    <CheckIcon color="primary" fontSize="small" />
                                  )}
                                </Box>
                                
                                {/* Color Preview */}
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  <Box
                                    sx={{
                                      width: isMobile ? 30 : 40,
                                      height: isMobile ? 30 : 40,
                                      borderRadius: 1,
                                      bgcolor: preset.colors.primary,
                                      border: '2px solid white',
                                      boxShadow: 1,
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      width: isMobile ? 30 : 40,
                                      height: isMobile ? 30 : 40,
                                      borderRadius: 1,
                                      bgcolor: preset.colors.secondary,
                                      border: '2px solid white',
                                      boxShadow: 1,
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      width: isMobile ? 30 : 40,
                                      height: isMobile ? 30 : 40,
                                      borderRadius: 1,
                                      bgcolor: preset.colors.success,
                                      border: '2px solid white',
                                      boxShadow: 1,
                                      flexShrink: 0,
                                    }}
                                  />
                                </Box>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </>
            ) : (
              <>
                {/* Custom Color Picker */}
                <Card variant="outlined" sx={{ mt: 2, width: '100%' }}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Create your own custom color scheme
                    </Typography>
                    
                    <Box sx={{ width: '100%' }}>
                      <Grid container spacing={isMobile ? 2 : 3}>
                        <Grid item xs={12} md={6}>
                          <ColorPicker
                            label="Primary Color"
                            value={tempCustomColors.primary}
                            onChange={(color) => setTempCustomColors({ ...tempCustomColors, primary: color })}
                            disabled={saving}
                          />
                          <ColorPicker
                            label="Secondary Color"
                            value={tempCustomColors.secondary}
                            onChange={(color) => setTempCustomColors({ ...tempCustomColors, secondary: color })}
                            disabled={saving}
                          />
                          <ColorPicker
                            label="Success Color"
                            value={tempCustomColors.success || '#4CAF50'}
                            onChange={(color) => setTempCustomColors({ ...tempCustomColors, success: color })}
                            disabled={saving}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <ColorPicker
                            label="Warning Color"
                            value={tempCustomColors.warning || '#FF9800'}
                            onChange={(color) => setTempCustomColors({ ...tempCustomColors, warning: color })}
                            disabled={saving}
                          />
                          <ColorPicker
                            label="Error Color"
                            value={tempCustomColors.error || '#F44336'}
                            onChange={(color) => setTempCustomColors({ ...tempCustomColors, error: color })}
                            disabled={saving}
                          />
                          <ColorPicker
                            label="Info Color"
                            value={tempCustomColors.info || '#2196F3'}
                            onChange={(color) => setTempCustomColors({ ...tempCustomColors, info: color })}
                            disabled={saving}
                          />
                        </Grid>
                      </Grid>
                    </Box>

                    <Stack 
                      direction={isMobile ? 'column' : 'row'} 
                      spacing={isMobile ? 1.5 : 2} 
                      sx={{ mt: 3, width: '100%' }}
                    >
                      <Button
                        variant="contained"
                        startIcon={!isMobile && <SaveIcon />}
                        onClick={handleApplyCustom}
                        disabled={saving}
                        fullWidth
                        size={isMobile ? 'small' : 'medium'}
                      >
                        {saving ? 'Saving...' : 'Apply Custom Colors'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => setShowCustom(false)}
                        disabled={saving}
                        fullWidth={isMobile}
                        size={isMobile ? 'small' : 'medium'}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </>
            )}

            <Divider sx={{ my: isMobile ? 2 : 3 }} />

            {/* Preview Section */}
            <Box sx={{ width: '100%' }}>
              <Typography variant={isMobile ? 'body2' : 'body1'} fontWeight={600} gutterBottom>
                Theme Preview
              </Typography>
              <Typography variant="caption" color="text.secondary" paragraph>
                See how your theme looks
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2, width: '100%' }}>
                <Chip label="Primary" color="primary" size="small" />
                <Chip label="Secondary" color="secondary" size="small" />
                <Chip label="Success" color="success" size="small" />
                <Chip label="Warning" color="warning" size="small" />
                <Chip label="Error" color="error" size="small" />
                <Chip label="Info" color="info" size="small" />
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                <Button variant="contained" size="small">Contained</Button>
                <Button variant="outlined" size="small">Outlined</Button>
                <Button variant="text" size="small">Text</Button>
              </Box>
            </Box>

            <Divider sx={{ my: isMobile ? 2 : 3 }} />

            {/* Reset Button */}
            <Box sx={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end', width: '100%' }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={!isMobile && <ResetIcon />}
                onClick={handleReset}
                disabled={saving}
                size={isMobile ? 'small' : 'medium'}
                fullWidth={isMobile}
              >
                Reset to Default
              </Button>
            </Box>
          </Paper>
        </TabPanel>

        {/* Notifications Tab */}
        <TabPanel value={currentTab} index={2} isMobile={isMobile}>
          <Paper sx={{ p: isMobile ? 1.5 : 3, width: '100%', boxSizing: 'border-box' }}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="bold" gutterBottom>
              Notification Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Coming soon - Configure company-wide notification preferences
            </Typography>
          </Paper>
        </TabPanel>

        {/* Security Tab */}
        <TabPanel value={currentTab} index={3} isMobile={isMobile}>
          <PasswordChangeSection isMobile={isMobile} />
        </TabPanel>
      </Container>
    </Box>
  );
};

export default CompanySettingsPage;